import { useRef, useEffect, RefObject } from 'react';

export const useDraggable = <T extends HTMLElement = HTMLDivElement>(
	handleRef: RefObject<HTMLElement | null>,
	options?: { getDisabled?: () => boolean; anchor?: 'top' | 'bottom' }
): { elementRef: RefObject<T | null> } => {
	const elementRef = useRef<T | null>(null);

	useEffect(() => {
		const handle = handleRef.current;
		const element = elementRef.current;
		if (!handle || !element) return;

		/* 'top' grows content downward after drag (default, for ModulePanel); 'bottom'
		   keeps the element pinned by its bottom edge so content grows upward (for toasts). */
		const anchor = options?.anchor ?? 'top';

		let dragging = false;
		let startMouseX = 0;
		let startMouseY = 0;
		let startLeft = 0;
		let startTop = 0;
		let startBottom = 0;

		const onMouseDown = (e: MouseEvent) => {
			if ((e.target as HTMLElement).closest('button')) return;
			if (options?.getDisabled?.()) return;
			e.preventDefault();

			const rect = element.getBoundingClientRect();

			/* Switch to explicit left + vertical anchoring. rect already accounts for
			   any existing transform, so we can clear it safely. */
			element.style.left = `${rect.left}px`;
			element.style.right = 'auto';
			element.style.transform = 'none';

			if (anchor === 'bottom') {
				startBottom = window.innerHeight - rect.bottom;
				element.style.bottom = `${startBottom}px`;
				element.style.top = 'auto';
			} else {
				startTop = rect.top;
				element.style.top = `${rect.top}px`;
				element.style.bottom = 'auto';
			}

			startMouseX = e.clientX;
			startMouseY = e.clientY;
			startLeft = rect.left;

			dragging = true;
		};

		const onMouseMove = (e: MouseEvent) => {
			if (!dragging) return;

			const winWidth = window.innerWidth;
			const winHeight = window.innerHeight;
			const width = element.offsetWidth;
			const height = element.offsetHeight;

			const rawLeft = startLeft + (e.clientX - startMouseX);
			const clampedLeft = Math.min(Math.max(0, rawLeft), winWidth - width);
			element.style.left = `${clampedLeft}px`;

			if (anchor === 'bottom') {
				// Dragging down reduces the bottom offset; clamp inside the viewport
				const rawBottom = startBottom - (e.clientY - startMouseY);
				const clampedBottom = Math.min(Math.max(0, rawBottom), winHeight - height);
				element.style.bottom = `${clampedBottom}px`;
			} else {
				const rawTop = startTop + (e.clientY - startMouseY);
				const clampedTop = Math.min(Math.max(0, rawTop), winHeight - height);
				element.style.top = `${clampedTop}px`;
			}
		};

		const onMouseUp = () => {
			dragging = false;
		};

		handle.addEventListener('mousedown', onMouseDown);
		document.addEventListener('mousemove', onMouseMove);
		document.addEventListener('mouseup', onMouseUp);

		return () => {
			handle.removeEventListener('mousedown', onMouseDown);
			document.removeEventListener('mousemove', onMouseMove);
			document.removeEventListener('mouseup', onMouseUp);
		};
	}, [handleRef]);

	return { elementRef };
};
